'use client';
import * as React from 'react';
import { useState } from 'react';
import { Table, Badge, Pagination, Avatar } from '@mantine/core';
import styles from './TeamProgressDashboard.module.scss';
import { useApiClient } from '../../api/ApiClientContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Spinner from '../spinner/Spinner';
import type { LeaderboardResponse, LeaderboardMemberResponse, DashboardResponse } from '../../api/Api';
import { LeaderboardEndorsementModal } from './LeaderboardEndorsementModal';
import { Icon } from '../../assets/icons/Icon';

const ITEMS_PER_PAGE = 8;

const AVATAR_COLORS = ['#7C3AED', '#10B981', '#F59E0B', '#3B82F6', '#EC4899', '#8B5CF6', '#06B6D4', '#EF4444'];

function getInitials(name: string): string {
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export function TeamProgressDashboard() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [endorseTarget, setEndorseTarget] = useState<LeaderboardMemberResponse | null>(null);

  const { data: leaderboardData, isLoading } = useQuery<LeaderboardResponse>({
    queryKey: ['teamLeaderboardV2'],
    queryFn: async () => {
      const res = await apiClient.api.getLeaderboardApiV1SocialLeaderboardGet({ view_type: 'team', limit: 50 });
      return res?.data;
    },
  });

  const { data: dashboardData } = useQuery<DashboardResponse>({
    queryKey: ['dashboard'],
    queryFn: async () => {
      const res = await apiClient.api.getDashboardDataApiV1DashboardGet();
      return res?.data;
    },
  });

  const teamName = dashboardData?.team_widget?.cohort_name;

  const handleEndorsed = () => {
    queryClient.invalidateQueries({ queryKey: ['teamLeaderboardV2'] });
  };

  if (isLoading) {
    return <Spinner />;
  }

  const entries = leaderboardData?.leaderboard || [];

  const activeMembers = entries.filter((e) => e.is_active_on_programme);
  const inactiveMembers = entries.filter((e) => !e.is_active_on_programme);
  const sortedEntries = [...activeMembers, ...inactiveMembers];

  const totalPages = Math.ceil(sortedEntries.length / ITEMS_PER_PAGE);
  const paginatedEntries = sortedEntries.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const getHabitBadges = (habits: LeaderboardMemberResponse['habits']) => {
    if (!habits || habits.length === 0) return null;

    const firstHabit = habits[0]?.habit_name || '';
    const remainingCount = habits.length - 1;

    return (
      <div className={styles.habitBadges}>
        <Badge variant="light" size="sm" className={styles.habitBadge}>
          {firstHabit}
        </Badge>
        {remainingCount > 0 && (
          <Badge variant="light" size="sm" className={styles.habitCountBadge}>
            +{remainingCount}
          </Badge>
        )}
      </div>
    );
  };

  const rows = paginatedEntries.map((entry) => {
    const isInactive = !entry.is_active_on_programme;
    const rowClass = isInactive ? styles.inactiveRow : '';

    return (
      <Table.Tr key={entry.user.id} className={rowClass}>
        <Table.Td>
          <div className={styles.nameCell}>
            <Avatar
              src={entry.user.avatar_url}
              size={36}
              radius="xl"
              color={getAvatarColor(entry.user.full_name)}
              className={styles.avatar}
            >
              {getInitials(entry.user.full_name)}
            </Avatar>
            <span className={styles.userName}>{entry.user.full_name}</span>
          </div>
        </Table.Td>
        <Table.Td>
          {isInactive ? (
            <span className={styles.inactiveText}>Not active</span>
          ) : (
            <span className={styles.activeDays}>
              {entry.active_days} {entry.active_days === 1 ? 'day' : 'days'} logged
            </span>
          )}
        </Table.Td>
        <Table.Td>
          {!isInactive && getHabitBadges(entry.habits)}
        </Table.Td>
        <Table.Td className={styles.endorseCell}>
          {!isInactive && !entry.is_current_user && entry.habits && entry.habits.length > 0 && (
            <button
              className={styles.endorseButton}
              disabled={entry.endorsed_all_habits_today}
              onClick={() => setEndorseTarget(entry)}
            >
              {entry.endorsed_all_habits_today ? 'Endorsed' : 'Endorse'}
            </button>
          )}
        </Table.Td>
      </Table.Tr>
    );
  });

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h2 className={styles.title}>My Team</h2>
        {teamName && <p className={styles.subtitle}>Team: {teamName}</p>}
      </div>

      <div className={styles.tableContainer}>
        <Table
          className={styles.leaderboardTable}
          verticalSpacing="md"
          horizontalSpacing="lg"
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th className={styles.nameHeader}>Name</Table.Th>
              <Table.Th className={styles.daysHeader}>Active days, last week</Table.Th>
              <Table.Th className={styles.habitsHeader}>Habits</Table.Th>
              <Table.Th className={styles.endorseHeader}>Endorsement</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{rows}</Table.Tbody>
        </Table>

        {totalPages > 1 && (
          <div className={styles.paginationContainer}>
            <Pagination
              total={totalPages}
              value={currentPage}
              onChange={setCurrentPage}
              size="sm"
              radius="sm"
              withEdges={false}
              siblings={1}
              boundaries={1}
            />
          </div>
        )}
      </div>

      {endorseTarget && (
        <LeaderboardEndorsementModal
          isOpen={!!endorseTarget}
          onClose={() => setEndorseTarget(null)}
          member={endorseTarget}
          onEndorsed={handleEndorsed}
        />
      )}
    </div>
  );
}

export default TeamProgressDashboard;
